(() => {
  if (customElements.get('showcase-wall')) return;

  class ShowcaseWall extends HTMLElement {
    connectedCallback() {
      // Wait for parser-created children if this script is re-run by the theme editor.
      if (!this.querySelector('[data-wall-grid]')) {
        this.pendingInit = requestAnimationFrame(() => this.connectedCallback());
        return;
      }
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.items = [...this.querySelectorAll('[data-wall-item]')];
      this.tabs = [...this.querySelectorAll('[data-wall-tab]')];
      this.grid = this.querySelector('[data-wall-grid]');
      this.panel = this.grid.parentElement;
      this.more = this.querySelector('[data-wall-more]');
      this.status = this.querySelector('[data-wall-status]');
      this.dialog = this.querySelector('[data-wall-dialog]');
      this.stage = this.querySelector('[data-wall-stage]');
      this.info = this.querySelector('[data-wall-info]');
      this.position = this.querySelector('[data-wall-position]');
      this.activeTab = this.dataset.defaultTab || 'All';
      this.visibleBatches = 1;

      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-wall-tab]');
        if (tab) return this.selectTab(tab.dataset.wallTab);
        if (event.target.closest('[data-wall-more]')) return this.loadMore();
        if (event.target.closest('[data-wall-close]')) return this.dialog.close();
        if (event.target.closest('[data-wall-prev]')) return this.move(-1);
        if (event.target.closest('[data-wall-next]')) return this.move(1);
        const card = event.target.closest('[data-wall-item]');
        if (card && !event.target.closest('a')) this.open(card);
      }, options);
      this.querySelector('[role="tablist"]')?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const index = this.tabs.indexOf(event.target);
        if (index < 0) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.selectTab(this.tabs[next].dataset.wallTab);
        this.tabs[next].focus();
      }, options);
      this.dialog.addEventListener('keydown', event => this.onDialogKey(event), options);
      this.dialog.addEventListener('close', () => this.onClose(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog || event.target === this.querySelector('[data-wall-lightbox]')) this.dialog.close();
      }, options);
      this.stage.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' && event.isPrimary) this.swipeStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, options);
      this.stage.addEventListener('pointerup', event => {
        const start = this.swipeStart;
        this.swipeStart = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.move(dx < 0 ? 1 : -1);
      }, options);
      this.stage.addEventListener('pointercancel', () => { this.swipeStart = null; }, options);
      document.addEventListener('shopify:block:select', event => {
        const card = this.items.find(item => item.dataset.blockId === event.detail?.blockId);
        if (!card) return;
        this.selectTab('All');
        this.visibleBatches = Math.ceil((this.items.indexOf(card) + 1) / this.perLoad);
        this.filter();
        card.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, options);

      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      this.resizeObserver.observe(this.grid);
      this.items.forEach(item => this.resizeObserver.observe(item.querySelector('[data-wall-card-inner]')));
      document.fonts?.ready.then(() => { if (this.isConnected) this.scheduleLayout(); });
      this.filter(false);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.pendingInit);
      cancelAnimationFrame(this.layoutFrame);
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.onClose();
      this.controller?.abort();
      this.controller = null;
    }

    selectTab(category) {
      if (category === this.activeTab) return;
      this.activeTab = category;
      this.visibleBatches = 1;
      this.filter();
    }

    filter(announce = true) {
      this.filtered = this.items.filter(item => this.activeTab === 'All' || item.dataset.category === this.activeTab);
      if (!this.filtered.length && this.activeTab !== 'All') {
        this.activeTab = 'All';
        this.filtered = this.items;
      }
      this.columns = Number(getComputedStyle(this.grid).getPropertyValue('--wall-columns')) || 2;
      // Each batch contains two complete rows at the current screen width.
      this.perLoad = this.columns * 2;
      this.visibleLimit = this.visibleBatches * this.perLoad;
      this.items.forEach(item => { item.hidden = !this.filtered.slice(0, this.visibleLimit).includes(item); });
      this.tabs.forEach(tab => {
        const selected = tab.dataset.wallTab === this.activeTab;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected) this.panel.setAttribute('aria-labelledby', tab.id);
      });
      this.more.hidden = this.filtered.length <= this.visibleLimit;
      if (announce) this.status.textContent = `Showing ${Math.min(this.visibleLimit, this.filtered.length)} of ${this.filtered.length} items in ${this.activeTab}.`;
      this.scheduleLayout();
    }

    loadMore() {
      const firstNew = this.filtered[this.visibleLimit];
      const buttonHadFocus = document.activeElement === this.more;
      this.visibleBatches += 1;
      this.filter();
      // Keep keyboard focus meaningful when the last load hides its button.
      if (buttonHadFocus && this.more.hidden) firstNew?.querySelector('[data-wall-open]').focus({ preventScroll: true });
    }

    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const styles = getComputedStyle(this.grid);
        const gap = parseFloat(styles.columnGap) || 24;
        const columns = Number(styles.getPropertyValue('--wall-columns')) || 2;
        if (columns !== this.columns) {
          this.filter(false);
          return;
        }
        const nextRow = Array(columns).fill(1);
        const visible = this.items.filter(item => !item.hidden);
        this.grid.dataset.masonryReady = '';
        // Fix each group's left-to-right columns before measuring after a resize.
        visible.forEach((item, index) => { item.style.gridColumnStart = String(index % columns + 1); });
        const heights = visible.map(item => item.querySelector('[data-wall-card-inner]').getBoundingClientRect().height);
        visible.forEach((item, index) => {
          const column = index % columns;
          const span = Math.ceil((heights[index] + gap) / 4);
          item.style.gridRowStart = String(nextRow[column]);
          item.style.gridRowEnd = `span ${span}`;
          nextRow[column] += span;
        });
      });
    }

    open(card) {
      this.currentIndex = this.filtered.indexOf(card);
      if (this.currentIndex < 0) return;
      this.returnFocus = card.querySelector('[data-wall-open]');
      this.previousOverflow = document.body.style.overflow;
      this.previousRootOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      this.renderCurrent();
      this.querySelector('[data-wall-close]').focus();
    }

    unloadVideo() {
      this.stage?.querySelectorAll('video').forEach(video => {
        video.pause();
        video.removeAttribute('autoplay');
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      });
      this.stage?.replaceChildren();
    }

    renderCurrent() {
      this.unloadVideo();
      const card = this.filtered[this.currentIndex];
      this.stage.append(card.querySelector('[data-wall-media]').content.cloneNode(true));
      this.info.replaceChildren(card.querySelector('[data-wall-details]').content.cloneNode(true));
      this.info.querySelector('[data-wall-dialog-title]').id = this.dialog.getAttribute('aria-labelledby');
      this.position.textContent = `${this.currentIndex + 1} / ${this.filtered.length}`;
      const onlyOne = this.filtered.length < 2;
      this.querySelector('[data-wall-prev]').hidden = onlyOne;
      this.querySelector('[data-wall-next]').hidden = onlyOne;
      const video = this.stage.querySelector('video');
      if (video) {
        video.muted = false;
        video.defaultMuted = false;
        video.volume = 1;
        video.play().catch(() => { /* Native controls remain available if the device blocks autoplay. */ });
      }
    }

    move(direction) {
      if (!this.dialog.open || this.filtered.length < 2) return;
      this.currentIndex = (this.currentIndex + direction + this.filtered.length) % this.filtered.length;
      this.renderCurrent();
    }

    onDialogKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dialog.close();
        return;
      }
      // Preserve arrow keys on native video controls for seeking/volume.
      if (['ArrowLeft', 'ArrowRight'].includes(event.key) && event.target.tagName !== 'VIDEO') {
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }
      if (event.key !== 'Tab') return;
      const controls = [...this.dialog.querySelectorAll('button, a[href], video[controls], [tabindex="0"]')].filter(element => !element.hidden && element.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    onClose() {
      this.unloadVideo();
      this.info?.replaceChildren();
      this.swipeStart = null;
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow;
        document.documentElement.style.overflow = this.previousRootOverflow;
        this.previousOverflow = undefined;
        this.previousRootOverflow = undefined;
        if (this.returnFocus?.isConnected) {
          // A narrower screen can hide the clicked card while the dialog is open.
          const index = this.filtered.indexOf(this.returnFocus.closest('[data-wall-item]'));
          if (index >= this.visibleLimit) {
            this.visibleBatches = Math.ceil((index + 1) / this.perLoad);
            this.filter(false);
          }
          this.returnFocus.focus({ preventScroll: true });
        }
      }
    }
  }

  customElements.define('showcase-wall', ShowcaseWall);
})();
